/* ============================================================
   sl-shape-motion.js — shape-by studiolab · Layer 3 (motion engine)
   VERSION: 1.2.0  ·  studiolab library (reusable)

   Single, data-attribute-driven GSAP engine for the parametric shape
   system. Queries the DOM for shapes that requested motion (the
   `data-sl-motion` attribute written by the Layer-2 snippet) and applies
   the matching animation. Adding a motion type = one `case` here.

   HARD REQUIREMENTS (per spec):
   - Stack: GSAP + ScrollTrigger. Feature-detected — never assumed.
   - Graceful degradation: GSAP/ScrollTrigger absent → shapes static, no throw.
   - Reduced motion: ALL motion gated behind `gsap.matchMedia()` with
     `(prefers-reduced-motion: no-preference)`. Under reduced motion no tweens
     are created; on revert matchMedia cleans up tweens AND our listeners.
   - Entrances fire once (`once: true`). Ambient loops are viewport-gated
     (paused off-screen) and run mobile-INCLUSIVE but LIGHTENED (smaller
     amplitude / slower) on small screens — DS v4 §1.4 reverses the earlier
     "off on mobile" precedent (motion is the magic; mobile is not a downgrade
     tier). Hover-only motion (wobble) still skips touch — no hover to trigger
     it — which is a no-hover-on-touch fact, not the reversed precedent.
   - Animates ONLY transform / opacity / strokeDashoffset. No layout props.
   - Alpine owns state, GSAP owns movement. The static rotate/scale/offset
     live on `.sl-shape__inner`; the engine only ever drives the ROOT
     `.sl-shape` (translate/scale/rotate) — never the same transform on the
     same element. Each shape has exactly one motion, so root transforms
     never overlap each other either.

   Motion types: none · float · drift · parallax · draw · pop · hover-wobble.

   Idempotent + load-guarded (per-block <script> tags share one src).
   ============================================================ */
(function () {
  'use strict';

  if (window.__slShapeMotionLoaded) return;
  window.__slShapeMotionLoaded = true;

  var MOBILE = '(max-width: 749px)';

  function wireShapes() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    // Graceful degradation — no library, static shapes, no throw.
    if (!gsap || !ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    // GROOVI (2026-07-01): the Horizon 4.x desktop "squeeze" is RETIRED (see
    // assets/scroll-container.js SQUEEZE_ACTIVE = false) — the document scrolls
    // natively at every width, which is ScrollTrigger's default scroller. Set it
    // explicitly (idempotent with groovi-animations.js) to be order-independent.
    ScrollTrigger.defaults({ scroller: window });

    if (!window.__slShapeMM) window.__slShapeMM = gsap.matchMedia();

    window.__slShapeMM.add('(prefers-reduced-motion: no-preference)', function () {
      var isMobile = window.matchMedia(MOBILE).matches;
      var shapes = document.querySelectorAll(
        '[data-sl-motion]:not([data-sl-motion="none"]):not([data-sl-motion-ready])'
      );
      var teardowns = [];

      shapes.forEach(function (el) {
        el.setAttribute('data-sl-motion-ready', '');
        teardowns.push(function () { el.removeAttribute('data-sl-motion-ready'); });

        var cleanup;
        switch (el.getAttribute('data-sl-motion')) {
          case 'float':        buildFloat(el, gsap, ScrollTrigger, isMobile); break;
          case 'drift':        buildDrift(el, gsap, ScrollTrigger, isMobile); break;
          case 'parallax':     buildParallax(el, gsap, isMobile); break;
          case 'draw':         buildDraw(el, gsap); break;
          case 'pop':          buildPop(el, gsap); break;
          case 'hover-wobble': cleanup = buildWobble(el, gsap, isMobile); break;
        }
        if (cleanup) teardowns.push(cleanup);
      });

      // matchMedia revert (e.g. user enables reduced-motion) runs this.
      return function () { teardowns.forEach(function (fn) { fn(); }); };
    });
  }

  /* Ambient vertical bob — small amplitude, paused off-screen. Mobile-inclusive:
     lightened (smaller bob, slower) rather than killed (DS v4 §1.4). */
  function buildFloat(el, gsap, ScrollTrigger, isMobile) {
    var tween = gsap.to(el, {
      yPercent: isMobile ? -2.5 : -4, duration: isMobile ? 5 : 3,
      ease: 'sine.inOut', repeat: -1, yoyo: true, paused: true
    });
    ScrollTrigger.create({
      trigger: el, start: 'top bottom', end: 'bottom top',
      onToggle: function (self) { self.isActive ? tween.play() : tween.pause(); }
    });
  }

  /* Ambient diagonal drift — slower + gentler than float, viewport-gated.
     Mobile-inclusive: lightened (smaller drift, slower) rather than killed (DS v4 §1.4). */
  function buildDrift(el, gsap, ScrollTrigger, isMobile) {
    var tween = gsap.to(el, {
      xPercent: isMobile ? 2 : 3, yPercent: isMobile ? -2 : -3, duration: isMobile ? 8 : 5,
      ease: 'sine.inOut', repeat: -1, yoyo: true, paused: true
    });
    ScrollTrigger.create({
      trigger: el, start: 'top bottom', end: 'bottom top',
      onToggle: function (self) { self.isActive ? tween.play() : tween.pause(); }
    });
  }

  /* Depth parallax — moves against scroll via ScrollTrigger scrub. Reduced on mobile. */
  function buildParallax(el, gsap, isMobile) {
    var dist = isMobile ? 6 : 14;
    gsap.fromTo(el,
      { yPercent: dist },
      {
        yPercent: -dist, ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true }
      }
    );
  }

  /* Outline self-draws on entry. pathLength="1" normalizes across any size. Once. */
  function buildDraw(el, gsap) {
    var outline = el.querySelector('.sl-shape__outline');
    if (!outline) return;
    gsap.fromTo(outline,
      { strokeDasharray: 1, strokeDashoffset: 1 },
      {
        strokeDashoffset: 0, duration: 1.1, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 90%', once: true }
      }
    );
  }

  /* Elastic scale-in on entry. Drives the ROOT scale (inner keeps its static scale). Once. */
  function buildPop(el, gsap) {
    gsap.from(el, {
      scale: 0.6, opacity: 0, duration: 0.7, ease: 'back.out(1.7)',
      scrollTrigger: { trigger: el, start: 'top 90%', once: true }
    });
  }

  /* Micro-rotation wobble on hover. Root rotation composes with the inner's
     static rotation. No hover on touch → skip on mobile. Returns a cleanup
     that removes the listener (matchMedia revert calls it). */
  function buildWobble(el, gsap, isMobile) {
    if (isMobile) return null;
    var tl = gsap.timeline({ paused: true })
      .to(el, { rotation: 2.5, duration: 0.12, ease: 'power1.out' })
      .to(el, { rotation: -2, duration: 0.12, ease: 'power1.inOut' })
      .to(el, { rotation: 0, duration: 0.16, ease: 'power1.inOut' });
    var onEnter = function () { tl.restart(); };
    el.addEventListener('pointerenter', onEnter);
    return function () { el.removeEventListener('pointerenter', onEnter); tl.kill(); };
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', wireShapes);
    } else {
      wireShapes();
    }
    document.addEventListener('shopify:section:load', wireShapes);
  }

  init();
})();
